import React, { useReducer, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Stack,
  Divider,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Avatar,
  Tab,
  Tabs,
  Badge,
} from "@mui/material";
import {
  Person,
  Palette,
  Language,
  Notifications,
  FormatSize,
  Key,
  Security,
  VpnKey,
  ArrowBack,
  Email,
  Campaign,
  Settings as SettingsIcon,
  Lock,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Initial state for the settings
const initialState = {
  activeTab: "account",
  settings: {
    isDarkMode: false,
    language: "en",
    fontSize: 16,
    pushNotifications: true,
    emailNotifications: true,
    promoEmails: true,
    is2FAEnabled: false,
  },
  hasChanges: false,
  user: {
    name: "Jane Doe",
    email: "janedoe@example.com",
    memberSince: "January 2023",
  },
};

// Reducer function to handle state updates
const reducer = (state, action) => {
  switch (action.type) {
    case "SET_TAB":
      return { ...state, activeTab: action.payload };
    case "UPDATE_SETTING":
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.payload.key]: action.payload.value,
        },
        hasChanges: true,
      };
    case "RESET":
      return { ...initialState, user: state.user, hasChanges: false };
    case "SAVE":
    
      return { ...state, hasChanges: false };
    case "SET_INITIAL_DARK_MODE":
      return {
        ...state,
        settings: { ...state.settings, isDarkMode: action.payload },
      };
    default:
      return state;
  }
};

const Settings = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const isDarkMode = theme.palette.mode === "dark";

  // Sync initial dark mode with the theme
  useEffect(() => {
    dispatch({ type: "SET_INITIAL_DARK_MODE", payload: isDarkMode });
  }, [isDarkMode]);

  const handleUpdate = (key, value) => {
    dispatch({ type: "UPDATE_SETTING", payload: { key, value } });
  };

  const handleSave = () => {
    dispatch({ type: "SAVE" });
  };

  const handleReset = () => {
    dispatch({ type: "RESET" });
  };

  const handleTabChange = (event, newValue) => {
    dispatch({ type: "SET_TAB", payload: newValue });
  };

  // Render content based on the active tab
  const renderTabContent = () => {
    switch (state.activeTab) {
      case "account":
        return (
          <List disablePadding>
            <ListItemButton
              sx={{ borderRadius: 2, mb: 1 }}
              onClick={() => navigate("/profile")}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Person color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Account Details"
                secondary="Edit your profile, email, and name."
              />
            </ListItemButton>
            <ListItemButton sx={{ borderRadius: 2, mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <VpnKey color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Change Password"
                secondary="Update your account password for security."
              />
            </ListItemButton>
          </List>
        );
      case "preferences":
        return (
          <List disablePadding>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Palette />
                </ListItemIcon>
                <ListItemText
                  primary="Dark Mode"
                  secondary="Toggle between light and dark themes."
                />
              </Stack>
              <Switch
                checked={state.settings.isDarkMode}
                onChange={(e) => handleUpdate("isDarkMode", e.target.checked)}
              />
            </ListItem>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Language />
                </ListItemIcon>
                <ListItemText
                  primary="Language"
                  secondary="Change the app's language."
                />
              </Stack>
              <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                <Select
                  value={state.settings.language}
                  onChange={(e) => handleUpdate("language", e.target.value)}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                </Select>
              </FormControl>
            </ListItem>
            <ListItem
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ width: "100%" }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <FormatSize />
                </ListItemIcon>
                <ListItemText
                  primary="Font Size"
                  secondary="Adjust the text size for better readability."
                />
              </Stack>
              <Box sx={{ width: "100%", px: 1 }}>
                <Slider
                  value={state.settings.fontSize}
                  onChange={(e, val) => handleUpdate("fontSize", val)}
                  min={12}
                  max={24}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Box>
            </ListItem>
          </List>
        );
      case "security":
        return (
          <List disablePadding>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Security color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary="Add an extra layer of security to your account."
                />
              </Stack>
              <Switch
                checked={state.settings.is2FAEnabled}
                onChange={(e) => handleUpdate("is2FAEnabled", e.target.checked)}
              />
            </ListItem>
            <ListItemButton sx={{ borderRadius: 2, mt: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Key color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Active Sessions"
                secondary="Review and manage logged-in devices."
              />
            </ListItemButton>
          </List>
        );
      case "notifications":
        return (
          <List disablePadding>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Notifications />
                </ListItemIcon>
                <ListItemText
                  primary="Push Notifications"
                  secondary="Receive real-time alerts on your device."
                />
              </Stack>
              <Switch
                checked={state.settings.pushNotifications}
                onChange={(e) =>
                  handleUpdate("pushNotifications", e.target.checked)
                }
              />
            </ListItem>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Email />
                </ListItemIcon>
                <ListItemText
                  primary="Important Emails"
                  secondary="Essential updates about your account."
                />
              </Stack>
              <Switch
                checked={state.settings.emailNotifications}
                onChange={(e) =>
                  handleUpdate("emailNotifications", e.target.checked)
                }
              />
            </ListItem>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Campaign />
                </ListItemIcon>
                <ListItemText
                  primary="Promotional Emails"
                  secondary="News and offers about our products."
                />
              </Stack>
              <Switch
                checked={state.settings.promoEmails}
                onChange={(e) => handleUpdate("promoEmails", e.target.checked)}
              />
            </ListItem>
          </List>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter', sans-serif",
        color: theme.palette.text.primary,
        overflowY: "auto",
        background: isDarkMode
          ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
          : `linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)`,
        // Custom scrollbar for a polished look
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: isDarkMode ? "#888" : "#aaa",
          borderRadius: "10px",
          transition: "background 0.3s ease",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: isDarkMode ? "#555" : "#888",
        },
      }}
    >
      {/* Header with Logout Button */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: { xs: 1.5, md: 2 },
          bgcolor: isDarkMode
            ? "rgba(25, 25, 25, 0.6)"
            : "rgba(255,255,255,0.6)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: isDarkMode ? "none" : theme.shadows[1],
        }}
      >
        <Typography variant="h6" fontWeight={600} color="primary.main">
          Settings
        </Typography>
        <Button
          variant="text"
          color="secondary"
          onClick={() => navigate("/")}
          startIcon={<ArrowBack />}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              transform: "translateX(-4px)",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Main Content Container with Glassmorphism */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 3, md: 6 },
        }}
      >
        {/* Settings Card Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 6 },
            maxWidth: 1200,
            width: "100%",
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 8px 32px 0 rgba(0, 0, 0, 0.37)`,
            backdropFilter: "blur(20px)",
            border: `1px solid ${
              isDarkMode
                ? "rgba(255, 255, 255, 0.18)"
                : "rgba(255, 255, 255, 0.3)"
            }`,
            bgcolor: isDarkMode
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(255, 255, 255, 0.2)",
          }}
        >
          {/* Left Panel - User Info and Navigation */}
          <Box
            sx={{
              flexBasis: { md: "35%" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              textAlign: "center",
              bgcolor: "transparent",
            }}
          >
            {/* User Avatar */}
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={state.user.avatar || undefined}
                sx={{
                  width: 150,
                  height: 150,
                  bgcolor: theme.palette.primary.light,
                  fontSize: 50,
                  fontWeight: 700,
                  transition: "0.3s",
                  border: `4px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 0 10px ${theme.palette.primary.main}`,
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: `0 0 20px ${theme.palette.primary.main}`,
                  },
                }}
              >
                {!state.user.avatar &&
                  state.user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
              </Avatar>
            </Box>
            {/* User Details */}
            <Typography variant="h5" fontWeight={700} mt={3}>
              {state.user.name}
            </Typography>
            <Typography color="text.secondary" fontSize={14} mt={0.5}>
              Member since {state.user.memberSince}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/profile")}
              sx={{ textTransform: "none", mt: 2 }}
            >
              Edit Profile
            </Button>

            {/* Navigation Tabs */}
            <Divider sx={{ my: 4, width: "100%" }} />
            <Tabs
              value={state.activeTab}
              onChange={handleTabChange}
              aria-label="settings tabs"
              orientation="vertical"
              variant="scrollable"
              sx={{
                width: "100%",
                "& .MuiTabs-indicator": {
                  left: 0,
                  borderRadius: "0 4px 4px 0",
                  transition: "0.2s ease-in-out",
                },
              }}
            >
              <Tab
                label="Account"
                value="account"
                icon={<Person />}
                iconPosition="start"
                sx={{ textTransform: "none", alignItems: "flex-start", py: 2 }}
              />
              <Tab
                label="Preferences"
                value="preferences"
                icon={<SettingsIcon />}
                iconPosition="start"
                sx={{ textTransform: "none", alignItems: "flex-start", py: 2 }}
              />
              <Tab
                label="Security"
                value="security"
                icon={<Lock />}
                iconPosition="start"
                sx={{ textTransform: "none", alignItems: "flex-start", py: 2 }}
              />
              <Tab
                label="Notifications"
                value="notifications"
                icon={
                  <Badge
                    color="secondary"
                    variant="dot"
                    invisible={!state.settings.pushNotifications}
                  >
                    <Notifications />
                  </Badge>
                }
                iconPosition="start"
                sx={{ textTransform: "none", alignItems: "flex-start", py: 2 }}
              />
            </Tabs>
          </Box>

          {/* Right Panel - Settings Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: { xs: 2, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              bgcolor: isDarkMode
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(255, 255, 255, 0.1)",
              borderLeft: `1px solid ${
                isDarkMode
                  ? "rgba(255, 255, 255, 0.18)"
                  : "rgba(255, 255, 255, 0.3)"
              }`,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                mb={3}
                letterSpacing={1}
                color="text.primary"
              >
                {state.activeTab.charAt(0).toUpperCase() +
                  state.activeTab.slice(1)}{" "}
                Settings
              </Typography>
              <Box sx={{ p: 2 }}>{renderTabContent()}</Box>
            </Box>

            {/* Footer with Actions */}
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!state.hasChanges}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!state.hasChanges}
                disableElevation
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  color: "#fff",
                  px: 3,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                Save Changes
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
